import { useEffect, useId, useState } from 'react';
import { api } from '../../api/client';
import type { PaymentReconciliationPage } from '../../api/types';
import { Alert, Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import './payment-history.css';

const money = (cents: number | null) => cents == null ? 'Pendiente' : (cents / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
const date = (value: string) => new Date(value).toLocaleString('es-ES', { timeZone: 'UTC', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

function demoPage(month: string): PaymentReconciliationPage {
  return { month, timezone: 'UTC', page: 1, pageSize: 50, total: 3,
    summary: { verified: 1, pending: 1, review: 1, verifiedFeesCents: 124 },
    payments: [
      { id: 'ejemplo-carta', channel: 'menu', createdAt: `${month}-01T12:30:00Z`, amountCents: 5000, tipCents: 200, currency: 'eur', state: 'verified', reason: null, feeCents: 124, netCents: 5076, checkedAt: `${month}-01T12:35:00Z` },
      { id: 'ejemplo-terminal', channel: 'terminal', createdAt: `${month}-01T12:00:00Z`, amountCents: 4000, tipCents: 0, currency: 'eur', state: 'pending', reason: null, feeCents: null, netCents: null, checkedAt: null },
      { id: 'ejemplo-revision', channel: 'menu', createdAt: `${month}-01T11:30:00Z`, amountCents: 2500, tipCents: 0, currency: 'eur', state: 'review', reason: 'refund_or_dispute', feeCents: null, netCents: null, checkedAt: `${month}-01T12:35:00Z` },
    ] };
}

export function PaymentHistoryPanel({ licenseId, isDemo }: { licenseId: number; isDemo: boolean }) {
  const fieldId = useId();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [data, setData] = useState<PaymentReconciliationPage | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let current = true;
    setData(null);
    setError('');
    setLoading(true);
    if (!/^20\d{2}-(0[1-9]|1[0-2])$/.test(month)) {
      setError('Selecciona un mes válido.');
      setLoading(false);
      return;
    }
    const request = isDemo ? Promise.resolve(demoPage(month)) : api.get<PaymentReconciliationPage>(
      `/api/license/${licenseId}/connect/reconciliation?month=${encodeURIComponent(month)}&page=${page}`);
    request.then((result) => { if (current) setData(result); })
      .catch((err: unknown) => { if (current) setError(err instanceof Error ? err.message : 'No se pudieron cargar los cobros.'); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [licenseId, isDemo, month, page, reload]);

  return <section className="payment-history" aria-labelledby={`${fieldId}-heading`}>
    <h5 id={`${fieldId}-heading`}>Desglose de cobros</h5>
    <p className="muted">Consulta lo cobrado, la comisión total descontada y el neto de cada pago.
      Los importes pendientes aparecerán cuando se hayan comprobado. El neto de un pago no equivale a un ingreso en tu banco.</p>
    {isDemo && <Alert tone="info">Datos de ejemplo. Los importes ilustran el desglose y no son una oferta de tarifas.</Alert>}
    <div className="payment-history__filters">
      <div><label htmlFor={fieldId}>Mes de los cobros (UTC)</label>
        <input id={fieldId} type="month" min="2000-01" max="2099-12" value={month}
          onInput={(event) => { setMonth(event.currentTarget.value); setPage(1); }}
          onChange={(event) => { setMonth(event.target.value); setPage(1); }} /></div>
      <Button variant="secondary" size="sm" disabled={loading} onClick={() => setReload((value) => value + 1)}>Actualizar lista</Button>
    </div>
    {error && <div role="alert"><Alert tone="danger">{error}</Alert></div>}
    {loading && <p role="status">Cargando el desglose de cobros…</p>}
    {data && <>
      <p className="payment-history__summary" role="status">Comprobados: {data.summary.verified} · Pendientes: {data.summary.pending} · En revisión: {data.summary.review}</p>
      {data.total === 0 ? <p className="muted">No hay cobros registrados en este mes.</p> : <>
        <p>Comisiones comprobadas: <strong>{money(data.summary.verifiedFeesCents)}</strong>.
          <span className="muted"> El total excluye los pagos pendientes o en revisión.</span></p>
        <ul className="payment-history__list">
          {data.payments.map((payment) => <li key={`${payment.channel}:${payment.id}`}>
            <div className="payment-history__row-heading">
              <span><strong>{payment.channel === 'menu' ? 'Carta digital' : 'Datáfono'}</strong><br />
                <time dateTime={payment.createdAt}>{date(payment.createdAt)} UTC</time></span>
              <Badge tone={payment.state === 'verified' ? 'success' : payment.state === 'review' ? 'warning' : 'neutral'}>
                {payment.state === 'verified' ? 'Comprobado' : payment.state === 'review' ? 'En revisión' : 'Pendiente'}</Badge>
            </div>
            <dl className="payment-history__amounts">
              <div><dt>Cobrado</dt><dd>{money(payment.amountCents + payment.tipCents)}</dd>
                {payment.tipCents > 0 && <small>Incluye {money(payment.tipCents)} de propina</small>}</div>
              <div><dt>Comisión total</dt><dd>{money(payment.feeCents)}</dd></div>
              <div><dt>Neto del pago</dt><dd>{money(payment.netCents)}</dd></div>
            </dl>
            {payment.state === 'review' && <p className="muted">Hay ajustes o diferencias que deben revisarse antes de confirmar el neto.</p>}
            {payment.checkedAt && <p className="payment-history__checked muted">Última comprobación: {date(payment.checkedAt)} UTC</p>}
          </li>)}
        </ul>
        {data.total > data.pageSize && <nav className="payment-history__pagination" aria-label="Páginas del desglose de cobros">
          <Button variant="secondary" size="sm" disabled={page === 1 || loading} onClick={() => setPage((value) => value - 1)}>Anterior</Button>
          <span>Página {page} de {Math.ceil(data.total / data.pageSize)}</span>
          <Button variant="secondary" size="sm" disabled={page * data.pageSize >= data.total || loading} onClick={() => setPage((value) => value + 1)}>Siguiente</Button>
        </nav>}
      </>}
    </>}
  </section>;
}
